import type { TournamentListItem } from '@brush/api-client';
import { ChevronRight } from 'lucide-react';
import { StatusChip, TournamentTags } from './cells';

/**
 * One tournament, below `md`. The phone arrangement of a table row — not a degraded one.
 *
 * The table spends 384px on cell padding alone before a character of content, against the 366px
 * `p-8` leaves at the 430px reference width, so it can only live in a horizontal scroller: a
 * sideways gesture nested inside a vertically scrolling `<main>`, where a tournament's name and
 * its actions are never on screen together. The card collapses the four sortable columns into one
 * line of facts instead, which is the shape that costs no vertical chrome to gain readability.
 *
 * Date and time are one fact here, in that order, because within a day the running order *is* the
 * schedule — the same reason the list sorts by date carrying time as the secondary key.
 *
 * **Only Enter/Update results survives from the row's three actions.** Entering results is done
 * standing at the desk as an event finishes. The tag `edit` link and Import CSV are both reachable
 * from the detail view this card opens, and both are worse bets on a phone — a CSV import needs a
 * file to hand and a four-step wizard, and the edit link was a 10px tap target.
 */
export function TournamentCard({
  tournament,
  shownTags,
  platformTags,
  onOpen,
  onEnterResults,
}: {
  tournament: TournamentListItem;
  /** The tournament's tags ordered for display, filtered ones first. */
  shownTags: string[];
  platformTags: string[];
  onOpen: () => void;
  onEnterResults: () => void;
}) {
  const t = tournament;

  return (
    <div className="hover:bg-felt-50/30">
      {/*
       * The whole card opens the tournament, and the results action sits *outside* this button
       * rather than inside it — a button within a button is invalid, which is also why the table
       * row puts its `onClick` on individual cells instead of wrapping the `<tr>`.
       */}
      <button type="button" onClick={onOpen} className="block w-full px-4 pt-4 text-left">
        <div className="flex items-start justify-between gap-3">
          <span className="font-medium text-ink-950">{t.name}</span>
          <span className="flex shrink-0 items-center gap-2">
            <StatusChip status={t.status} />
            <ChevronRight className="size-4 text-ink-800/30" aria-hidden />
          </span>
        </div>

        {/* One line of facts, in the order the list is scanned: when, then what it costs, then
            how big it was. Entries are omitted rather than shown as 0 — no room has run a
            tournament nobody entered, so a zero there could only read as a fault. */}
        <div className="num mt-1 text-xs text-ink-800/70">
          {t.date} · {t.time} · ${t.buyIn.toLocaleString()}
          {t.entries > 0 ? ` · ${t.entries.toLocaleString()} entries` : ''}
        </div>

        {t.tags.length > 0 ? (
          <div className="mt-2">
            <TournamentTags tags={t.tags} shown={shownTags} platformTags={platformTags} />
          </div>
        ) : null}
      </button>

      <div className="px-4 pb-3 pt-2">
        <button
          type="button"
          onClick={onEnterResults}
          className="text-xs font-medium text-felt-700 underline underline-offset-2 hover:text-felt-900"
        >
          {t.entries > 0 ? 'Update results' : 'Enter results'}
        </button>
      </div>
    </div>
  );
}
